import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Trash2, X } from "lucide-react";
import ConfirmationModal from "../../../components/ConfirmationModal";
import CreatableCombobox from "../../../components/CreatableCombobox";
import { createCategory, updateCategory, deleteCategoryAttribute } from "../../../services/materialAdmin";

export const codeFromName = (name) =>
  (name || "").toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 20);

const ATTR_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "select", label: "Select (one of list)" },
  { value: "multiselect", label: "Multi-select" },
];

const EMPTY = {
  categoryCode: "", categoryName: "", defaultUnit: "", parentCategoryId: "",
  hasVariants: true, description: "", displayOrder: 0, isActive: true,
};

const emptyAttr = () => ({
  materialCategoryAttributeId: null, attributeName: "", attributeLabel: "",
  attributeType: "text", allowedValuesText: "", unit: "", isRequired: false,
  isFilterable: true, displayOrder: 0, isVariantAttribute: false, defaultValue: null,
});

const Field = ({ label, value, onChange, required, placeholder, type = "text" }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-flamingo-600">*</span>}
    </label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
    />
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
      className="rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500/40" />
    {label}
  </label>
);

/**
 * Create/Edit a material category, including its attribute definitions.
 * Shared by the Categories tab and the Add-Material inline "add category" flow.
 *
 * Props: open, onClose, onSaved(savedCategory), editing (category with attributes | null),
 *        categories ([{value,label}] for the parent picker), prefillName (inline-create).
 */
const CategoryModal = ({ open, onClose, onSaved, editing, categories = [], prefillName = "" }) => {
  const [form, setForm] = useState(EMPTY);
  const [attrs, setAttrs] = useState([]);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmAttr, setConfirmAttr] = useState(null); // {index, id, label}

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        categoryCode: editing.categoryCode, categoryName: editing.categoryName,
        defaultUnit: editing.defaultUnit, parentCategoryId: editing.parentCategoryId || "",
        hasVariants: editing.hasVariants ?? true, description: editing.description || "",
        displayOrder: editing.displayOrder ?? 0, isActive: editing.isActive ?? true,
      });
      setAttrs(
        (editing.attributes || []).map((a) => ({
          materialCategoryAttributeId: a.materialCategoryAttributeId,
          attributeName: a.attributeName, attributeLabel: a.attributeLabel,
          attributeType: a.attributeType,
          allowedValuesText: Array.isArray(a.allowedValues) ? a.allowedValues.join(", ") : "",
          unit: a.unit || "", isRequired: !!a.isRequired,
          isFilterable: a.isFilterable ?? true, displayOrder: a.displayOrder ?? 0,
          // Pass-through fields not edited in this UI — preserved so updates don't reset them.
          isVariantAttribute: !!a.isVariantAttribute, defaultValue: a.defaultValue ?? null,
        }))
      );
      setCodeTouched(true);
    } else {
      setForm({ ...EMPTY, categoryName: prefillName, categoryCode: codeFromName(prefillName) });
      setAttrs([]);
      setCodeTouched(false);
    }
  }, [open, editing, prefillName]);

  const setAttr = (i, key, val) => setAttrs((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const removeAttr = async () => {
    const { index, id } = confirmAttr;
    setConfirmAttr(null);
    if (id) {
      try {
        await deleteCategoryAttribute(editing.materialCategoryId, id);
        toast.success("Attribute deleted — existing values stay as free-form text");
      } catch (e) {
        return toast.error(e?.response?.data?.detail || "Failed to delete attribute");
      }
    }
    setAttrs((rows) => rows.filter((_, idx) => idx !== index));
  };

  const save = async () => {
    if (!form.categoryName?.trim() || !form.defaultUnit?.trim() || (!editing && !form.categoryCode?.trim())) {
      return toast.error("Code, name and default unit are required");
    }
    for (const a of attrs) {
      if (!a.attributeName?.trim() || !a.attributeLabel?.trim()) {
        return toast.error("Every attribute needs a name and a label");
      }
      if ((a.attributeType === "select" || a.attributeType === "multiselect") && !a.allowedValuesText?.trim()) {
        return toast.error(`Attribute "${a.attributeLabel}" needs allowed values (comma-separated)`);
      }
    }
    const attrPayload = attrs.map((a) => ({
      ...(a.materialCategoryAttributeId ? { materialCategoryAttributeId: a.materialCategoryAttributeId } : {}),
      attributeName: a.attributeName.trim(),
      attributeLabel: a.attributeLabel.trim(),
      attributeType: a.attributeType,
      allowedValues:
        a.attributeType === "select" || a.attributeType === "multiselect"
          ? a.allowedValuesText.split(",").map((v) => v.trim()).filter(Boolean)
          : null,
      unit: a.unit || null,
      isRequired: a.isRequired,
      isFilterable: a.isFilterable,
      isVariantAttribute: a.isVariantAttribute,
      defaultValue: a.defaultValue,
      displayOrder: Number(a.displayOrder) || 0,
    }));
    const payload = {
      categoryName: form.categoryName.trim(),
      defaultUnit: form.defaultUnit.trim(),
      parentCategoryId: form.parentCategoryId ? Number(form.parentCategoryId) : null,
      hasVariants: form.hasVariants,
      description: form.description || null,
      displayOrder: Number(form.displayOrder) || 0,
      isActive: form.isActive,
      attributes: attrPayload,
    };
    setSaving(true);
    try {
      let saved;
      if (editing) {
        saved = await updateCategory(editing.materialCategoryId, payload);
        toast.success("Category updated");
      } else {
        saved = await createCategory({ ...payload, categoryCode: form.categoryCode.trim().toUpperCase() });
        toast.success("Category created");
      }
      onSaved && (await onSaved(saved));
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[60] ${open ? "" : "hidden"}`}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900">{editing ? "Edit Category" : "Add Category"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name" value={form.categoryName} required placeholder="e.g. Tiles"
            onChange={(v) => setForm((p) => ({
              ...p, categoryName: v,
              categoryCode: !editing && !codeTouched ? codeFromName(v) : p.categoryCode,
            }))} />
          <Field label="Code" value={form.categoryCode} required={!editing} placeholder="TILES"
            onChange={(v) => { setCodeTouched(true); setForm({ ...form, categoryCode: v.toUpperCase() }); }} />
          {editing && (
            <p className="sm:col-span-2 -mt-3 text-[11px] text-slate-400">Code is fixed after creation (it's used in bulk-upload files).</p>
          )}
          <Field label="Default Unit" value={form.defaultUnit} required placeholder="sqft / nos / kg"
            onChange={(v) => setForm({ ...form, defaultUnit: v })} />
          <CreatableCombobox
            label="Parent Category"
            value={form.parentCategoryId}
            onChange={(v) => setForm({ ...form, parentCategoryId: v || "" })}
            options={categories.filter((c) => !editing || c.value !== editing.materialCategoryId)}
            placeholder="(none)"
          />
          <Field label="Display Order" type="number" value={form.displayOrder}
            onChange={(v) => setForm({ ...form, displayOrder: v })} />
          <div>
            <label className="text-xs font-medium text-slate-600">Description</label>
            <input value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500" />
          </div>
          <div className="sm:col-span-2 flex items-center gap-6">
            <Toggle label="Has variants" checked={form.hasVariants} onChange={(v) => setForm({ ...form, hasVariants: v })} />
            <Toggle label="Active" checked={form.isActive} onChange={(v) => setForm({ ...form, isActive: v })} />
          </div>

          {/* Attribute definitions builder */}
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mt-1 mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Attribute Definitions</p>
              <button type="button" onClick={() => setAttrs((rows) => [...rows, emptyAttr()])}
                className="inline-flex items-center gap-1 rounded-lg border border-flamingo-200 text-flamingo-600 text-xs font-semibold py-1.5 px-2.5 hover:bg-flamingo-50">
                <Plus size={13} /> Add attribute
              </button>
            </div>
            {attrs.length === 0 ? (
              <p className="text-xs text-slate-400">Define the fields every material in this category should carry (e.g. Thickness, Finish).</p>
            ) : (
              <div className="space-y-3">
                {attrs.map((a, i) => (
                  <div key={a.materialCategoryAttributeId ?? `new-${i}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <input className="rounded-lg border border-slate-300 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-flamingo-200"
                        placeholder="name (e.g. thickness)" value={a.attributeName}
                        onChange={(e) => setAttr(i, "attributeName", e.target.value)} disabled={!!a.materialCategoryAttributeId} />
                      <input className="rounded-lg border border-slate-300 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-flamingo-200"
                        placeholder="Label (e.g. Thickness)" value={a.attributeLabel}
                        onChange={(e) => setAttr(i, "attributeLabel", e.target.value)} />
                      <select className="rounded-lg border border-slate-300 bg-white text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-flamingo-200"
                        value={a.attributeType} onChange={(e) => setAttr(i, "attributeType", e.target.value)}>
                        {ATTR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      {(a.attributeType === "select" || a.attributeType === "multiselect") && (
                        <input className="sm:col-span-2 rounded-lg border border-slate-300 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-flamingo-200"
                          placeholder="Allowed values, comma-separated (Glossy, Matte, Satin)" value={a.allowedValuesText}
                          onChange={(e) => setAttr(i, "allowedValuesText", e.target.value)} />
                      )}
                      <input className="rounded-lg border border-slate-300 text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-flamingo-200"
                        placeholder="Unit (mm, gsm…)" value={a.unit}
                        onChange={(e) => setAttr(i, "unit", e.target.value)} />
                    </div>
                    <div className="flex items-center justify-between mt-2.5">
                      <div className="flex items-center gap-5">
                        <Toggle label="Required" checked={a.isRequired} onChange={(v) => setAttr(i, "isRequired", v)} />
                        <Toggle label="Filterable" checked={a.isFilterable} onChange={(v) => setAttr(i, "isFilterable", v)} />
                      </div>
                      <button type="button" title="Delete attribute"
                        onClick={() =>
                          a.materialCategoryAttributeId
                            ? setConfirmAttr({ index: i, id: a.materialCategoryAttributeId, label: a.attributeLabel })
                            : setAttrs((rows) => rows.filter((_, idx) => idx !== i))
                        }
                        className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
          <button onClick={onClose} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
          <button onClick={save} disabled={saving} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5">
            {saving ? "Saving…" : editing ? "Update" : "Create"}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirmAttr}
        onConfirm={removeAttr}
        onCancel={() => setConfirmAttr(null)}
        message={`Delete attribute "${confirmAttr?.label}"? Existing material values for it are kept as free-form text.`}
      />
    </div>
  );
};

export default CategoryModal;
